import { CaretDown, Code } from "@phosphor-icons/react"
import { SelectField } from "@tokenterminal/core/ui/input/SelectField"
import { useState } from "react"
import { PRODUCTS } from "@/constants/urls"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { MasonryWidget } from "./MasonryWidget"

const mockApiResponse = {
  data: [
    {
      timestamp: "2024-10-21T00:00:00.000Z",
      project_name: "Base",
      project_id: "base",
      fees: 107315.92,
      revenue: 107315.92,
      earnings: 106651.32,
      user_dau: 1662351,
    },
    {
      timestamp: "2024-10-22T00:00:00.000Z",
      project_name: "Base",
      project_id: "base",
      fees: 112458.34,
      revenue: 112458.34,
      earnings: 111789.12,
      user_dau: 1689452,
    },
  ],
}

const colors = {
  string: "#E5C07B",
  number: "#C678DD",
}

interface JsonLineProps {
  lineNumber: number
  content: React.ReactNode
}

function JsonLine({ lineNumber, content }: JsonLineProps) {
  return (
    <div className="flex">
      <span className="w-6 text-right pr-3 text-fg-secondary select-none shrink-0 text-[10px]">
        {lineNumber}
      </span>
      <span className="flex-1">{content}</span>
    </div>
  )
}

function JsonKey({ children }: { children: string }) {
  return <span className="text-fg-default">&quot;{children}&quot;</span>
}

function JsonString({ children }: { children: string }) {
  return <span style={{ color: colors.string }}>&quot;{children}&quot;</span>
}

function JsonNumber({ children }: { children: number }) {
  return <span style={{ color: colors.number }}>{children}</span>
}

function Indent({ level }: { level: number }) {
  return <span>{" ".repeat(level * 2)}</span>
}

export function MasonryAPIViewer() {
  const responses = mockApiResponse.data
  const [httpMethod, setHttpMethod] = useState("GET")
  const [apiUrl, setApiUrl] = useState("api.tokenterminal.com/v2/project/base/metrics")

  return (
    <MasonryWidget
      href={PRODUCTS.API_ROUTE}
      icon={Code}
      productName="API"
      widgetName="REST API"
      description="Access standardized blockchain metrics through a developer-friendly REST API with comprehensive documentation and type definitions."
      height={500}
      featuredVisual={
        <div
          className="relative border-t border-l border-solid border-border-default rounded-tl-xl bg-bg-default flex-1 min-h-0"
          style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
        >
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <SelectField
                aria-label="HTTP Method"
                selectedKey={httpMethod}
                onSelectionChange={(value) => setHttpMethod(value)}
                buttonProps={{
                  size: "small",
                  className: "w-20! shrink-0",
                }}
                popoverProps={{
                  placement: "bottom start",
                }}
              >
                <SelectField.Option id="GET">GET</SelectField.Option>
                <SelectField.Option id="POST">POST</SelectField.Option>
                <SelectField.Option id="PUT">PUT</SelectField.Option>
                <SelectField.Option id="DELETE">DELETE</SelectField.Option>
              </SelectField>
              <input
                type="text"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                className="flex-1 h-tt-small px-2.5 text-xs font-mono text-fg-default bg-bg-default border border-border-default rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
              />
            </div>
            <div className="flex items-center gap-2 font-mono text-xs mb-3">
              <span className="text-green-600 dark:text-[#1AFFAB]">200 OK</span>
              <span className="text-fg-secondary">•</span>
              <span className="text-fg-secondary">309 ms</span>
              <span className="text-fg-secondary">•</span>
              <span className="text-fg-secondary">535.2 KB</span>
            </div>

            <div className="flex items-center gap-4 mb-4 text-xs">
              <button className="flex items-center gap-1 text-fg-default">
                Pretty
                <CaretDown className="w-2.5 h-2.5" />
              </button>
              <span className="text-fg-secondary">
                Headers <span className="opacity-60">14</span>
              </span>
            </div>

            <div className="font-mono text-[11px] leading-relaxed whitespace-pre">
              <JsonLine lineNumber={1} content={"{"} />
              <JsonLine
                lineNumber={2}
                content={
                  <>
                    <Indent level={1} />
                    <JsonKey>data</JsonKey>: [
                  </>
                }
              />
              {responses.map((response, index) => {
                const baseLineNumber = 3 + index * 9
                return (
                  <div key={`response-${index}`}>
                    <JsonLine
                      lineNumber={baseLineNumber}
                      content={
                        <>
                          <Indent level={2} />
                          {"{"}
                        </>
                      }
                    />
                    <JsonLine
                      lineNumber={baseLineNumber + 1}
                      content={
                        <>
                          <Indent level={3} />
                          <JsonKey>timestamp</JsonKey>:{" "}
                          <JsonString>{response.timestamp}</JsonString>,
                        </>
                      }
                    />
                    <JsonLine
                      lineNumber={baseLineNumber + 2}
                      content={
                        <>
                          <Indent level={3} />
                          <JsonKey>project_name</JsonKey>:{" "}
                          <JsonString>{response.project_name}</JsonString>,
                        </>
                      }
                    />
                    <JsonLine
                      lineNumber={baseLineNumber + 3}
                      content={
                        <>
                          <Indent level={3} />
                          <JsonKey>project_id</JsonKey>:{" "}
                          <JsonString>{response.project_id}</JsonString>,
                        </>
                      }
                    />
                    <JsonLine
                      lineNumber={baseLineNumber + 4}
                      content={
                        <>
                          <Indent level={3} />
                          <JsonKey>fees</JsonKey>: <JsonNumber>{response.fees}</JsonNumber>,
                        </>
                      }
                    />
                    <JsonLine
                      lineNumber={baseLineNumber + 5}
                      content={
                        <>
                          <Indent level={3} />
                          <JsonKey>revenue</JsonKey>: <JsonNumber>{response.revenue}</JsonNumber>,
                        </>
                      }
                    />
                    <JsonLine
                      lineNumber={baseLineNumber + 6}
                      content={
                        <>
                          <Indent level={3} />
                          <JsonKey>earnings</JsonKey>: <JsonNumber>{response.earnings}</JsonNumber>,
                        </>
                      }
                    />
                    <JsonLine
                      lineNumber={baseLineNumber + 7}
                      content={
                        <>
                          <Indent level={3} />
                          <JsonKey>user_dau</JsonKey>: <JsonNumber>{response.user_dau}</JsonNumber>,
                        </>
                      }
                    />
                    <JsonLine
                      lineNumber={baseLineNumber + 8}
                      content={
                        <>
                          <Indent level={2} />
                          {index < responses.length - 1 ? "}," : "}"}
                        </>
                      }
                    />
                  </div>
                )
              })}
              <JsonLine
                lineNumber={3 + responses.length * 9}
                content={
                  <>
                    <Indent level={1} />]
                  </>
                }
              />
              <JsonLine lineNumber={4 + responses.length * 9} content={"}"} />
            </div>
          </div>
        </div>
      }
    />
  )
}
